import React from 'react';
import { useNavigate } from 'react-router-dom';
import './BloodBanksSection.css';

const BloodBanksSection = () => {
    const navigate = useNavigate();

    const handleViewBloodBanks = () => {
        navigate('/blood-banks');
    };

    const bloodBankStats = [
        { number: '500+', label: 'Verified Blood Banks', icon: '🏥' },
        { number: '24/7', label: 'Emergency Services', icon: '🚨' },
        { number: '10K+', label: 'Lives Saved', icon: '❤️' },
        { number: '50+', label: 'Cities Covered', icon: '🌍' }
    ];

    const bloodTypes = [
        { type: 'A+', availability: 85, urgent: false },
        { type: 'A-', availability: 45, urgent: false },
        { type: 'B+', availability: 78, urgent: false },
        { type: 'B-', availability: 38, urgent: true },
        { type: 'AB+', availability: 92, urgent: false },
        { type: 'AB-', availability: 25, urgent: true },
        { type: 'O+', availability: 88, urgent: false },
        { type: 'O-', availability: 35, urgent: true }
    ];

    const featuredBloodBanks = [
        {
            name: 'Apollo Blood Bank',
            location: 'Bangalore',
            distance: '2.5 km',
            availability: 'High',
            emergencyService: true,
            rating: 4.8
        },
        {
            name: 'Fortis Blood Center',
            location: 'Mumbai',
            distance: '4.1 km',
            availability: 'Medium',
            emergencyService: false,
            rating: 4.5
        }
    ];

    return (
        <section className="blood-banks-section meddylist-bloodbanks">
            <div className="container">
                <div className="row align-items-center">
                    <div className="col-lg-6">
                        <div className="blood-banks-content">
                            <div className="section-badge">
                                <span className="badge-icon">🩸</span>
                                <span className="badge-text">Blood Banks</span>
                            </div>
                            
                            <h2 className="section-title">
                                Find Blood Banks
                                <span className="highlight"> Near You</span>
                            </h2>
                            
                            <p className="section-description">
                                Locate verified blood banks in your area and check real-time blood availability. 
                                Connect with life-saving resources when time matters most.
                            </p>

                            <div className="blood-types-preview">
                                <h4 className="preview-title">Blood Type Availability</h4>
                                <div className="blood-types-grid">
                                    {bloodTypes.slice(0, 6).map((blood, index) => (
                                        <div key={index} className={`blood-type-item ${blood.urgent ? 'urgent' : ''}`}>
                                            <div className="blood-type">{blood.type}</div>
                                            <div className="availability-bar">
                                                <div 
                                                    className="availability-fill"
                                                    style={{ width: `${blood.availability}%` }}
                                                />
                                            </div>
                                            <div className="availability-percent">{blood.availability}%</div>
                                            {blood.urgent && <div className="urgent-indicator">!</div>}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="featured-banks">
                                <h4 className="featured-title">Nearby Blood Banks</h4>
                                <div className="banks-list">
                                    {featuredBloodBanks.map((bank, index) => (
                                        <div key={index} className="bank-preview">
                                            <div className="bank-info">
                                                <div className="bank-name">{bank.name}</div>
                                                <div className="bank-location">{bank.location} • {bank.distance}</div>
                                                <div className="bank-details">
                                                    <span className={`availability-badge ${bank.availability.toLowerCase()}`}>
                                                        {bank.availability} Availability
                                                    </span>
                                                    {bank.emergencyService && (
                                                        <span className="emergency-badge">24/7 Emergency</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="bank-rating">
                                                <span className="rating-stars">
                                                    {'★'.repeat(Math.floor(bank.rating))}
                                                    {bank.rating % 1 >= 0.5 ? '☆' : ''}
                                                </span>
                                                <span className="rating-number">{bank.rating}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="cta-section">
                                <button 
                                    className="btn btn-primary btn-lg me-3"
                                    onClick={handleViewBloodBanks}
                                >
                                    Search Blood Banks
                                </button>
                                <button 
                                    className="btn btn-outline-primary btn-lg"
                                    onClick={handleViewBloodBanks}
                                >
                                    Emergency Blood
                                </button>
                            </div>

                            <div className="trust-indicators">
                                <span className="trust-text">Saving lives through blood connectivity</span>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-6">
                        <div className="stats-container">
                            <div className="stats-grid">
                                {bloodBankStats.map((stat, index) => (
                                    <div key={index} className="stat-card">
                                        <div className="stat-icon">{stat.icon}</div>
                                        <div className="stat-number">{stat.number}</div>
                                        <div className="stat-label">{stat.label}</div>
                                    </div>
                                ))}
                            </div>
                            
                            <div className="blood-bank-hero-image">
                                <div className="featured-blood-bank">
                                    <div className="blood-bank-featured-tag">
                                        <div className="blood-bank-icon">🚨</div>
                                        <div className="blood-bank-text">
                                            <div className="blood-bank-title-featured">Emergency Blood</div>
                                            <div className="blood-bank-subtitle">Available 24/7</div>
                                            <div className="blood-bank-highlights">
                                                <span className="highlight-badge-urgent">Emergency</span>
                                                <span className="response-badge">Quick Response</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="blood-donation-counter">
                                        <span className="counter-icon">❤️</span>
                                        <span className="counter-text">10,247 Lives Saved</span>
                                    </div>
                                </div>

                                <div className="floating-blood-elements">
                                    <div className="floating-blood-badge badge-blood-1">
                                        <span>O+ Available</span>
                                    </div>
                                    <div className="floating-blood-badge badge-blood-2">
                                        <span>A+ Available</span>
                                    </div>
                                    <div className="floating-blood-badge badge-blood-3">
                                        <span>Emergency</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default BloodBanksSection;

